import React from 'react';
import {
    PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
} from 'recharts';
import { BreakdownDataPoint } from './BreakdownChart';
import { CATEGORICAL_COLORS } from './categoricalColors';

interface DonutChartProps {
    data: BreakdownDataPoint[];
    // Maps a raw group key to its display label
    groupLabels: Record<string, string>;
    height?: number;
    valueFormatter?: (value: number) => string;
    // 'side' places the legend to the right of the donut instead of below it, for widgets wider than 1 column
    legendPosition?: 'bottom' | 'side';
}

// Renders the rounded, dark tooltip shown on hover
const CustomTooltip: React.FC<any> = ({ active, payload }) => {
    if (!active || !payload || !payload.length) {
        return null;
    }

    const point = payload[0];
    return (
        <div className='time-series-tooltip'>
            <div className='tooltip-row'>
                <span className='tooltip-dot' style={{ backgroundColor: point.payload.fill }} />
                <span className='tooltip-name'>{point.name}</span>
                <span className='tooltip-value'>{point.value}</span>
            </div>
        </div>
    );
};

const DonutChart: React.FC<DonutChartProps> = ({ data, groupLabels, height = 220, valueFormatter, legendPosition = 'bottom' }) => {
    const formatValue = valueFormatter || ((value: number) => value.toString());

    // Resolve the display label and a stable color per group, plus each group's share of the total
    const total = data.reduce((sum, point) => sum + point.value, 0);
    const chartData = data.map((point, index) => ({
        ...point,
        label: groupLabels[point.group] ?? point.group,
        fill: CATEGORICAL_COLORS[index % CATEGORICAL_COLORS.length],
        percentage: total > 0 ? Math.round((point.value / total) * 100) : 0,
    }));

    return (
        <div className={`donut-chart${legendPosition === 'side' ? ' legend-side' : ''}`}>
            <ResponsiveContainer width='100%' height={height}>
                <PieChart>
                    <Pie
                        data={chartData}
                        dataKey='value'
                        nameKey='label'
                        innerRadius='55%'
                        outerRadius='90%'
                        paddingAngle={2}
                        stroke='none'
                        isAnimationActive
                        animationDuration={400}
                    >
                        {chartData.map(point => (
                            <Cell key={point.group} fill={point.fill} />
                        ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                </PieChart>
            </ResponsiveContainer>
            <div className='donut-chart-legend'>
                {chartData.map(point => (
                    <div key={point.group} className='donut-chart-legend-item'>
                        <span className='dot' style={{ backgroundColor: point.fill }} />
                        <span>{point.label} ({formatValue(point.value)} · {point.percentage}%)</span>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default DonutChart;
